/**
 * Contrato da documentação do pré-credenciado (frente F10, D-28) — o que a API
 * `bff/v1/documentacao-pre-credenciado` devolve e recebe. Sem React. Uma fonte
 * só para as duas telas: a do DTP (`/operacao/pre-credenciamento`, aqui) e a
 * do portal (`/credenciado/pre-credenciamento`,
 * `modulos/portal-credenciado/pre-credenciamento/`).
 *
 * Formato e regras: docs/credenciamento-ponta-a-ponta/frentes/F10.md, seção 2.
 * Toda regra (quem é o dono, validade, correlação da trinca) é do banco (0248).
 */

export const API_DOCUMENTACAO = '/bff/v1/documentacao-pre-credenciado';
export const API_PORTAL = `${API_DOCUMENTACAO}/portal`;
export const API_DTP = `${API_DOCUMENTACAO}/dtp`;

export type Natureza = 'PF' | 'PJ' | 'COOPERATIVA';
export type Frente = 'ENTIDADE' | 'VEICULO' | 'CONDUTOR' | 'MONITOR';
export type SituacaoDossie = 'EM_PREENCHIMENTO' | 'APTO_CONVOCACAO' | 'HABILITADO' | 'ENCERRADO';
export type SituacaoFrente = 'COMPLETA' | 'PENDENTE' | 'SEM_PROFISSIONAL';
/** Situação do requisito: a da versão atual, ou uma derivada (PENDENTE, VENCIDO, SEM_PROFISSIONAL). */
export type SituacaoItem =
  | 'PENDENTE'
  | 'EM_ANALISE'
  | 'APROVADO'
  | 'RECUSADO'
  | 'COMPLEMENTAR'
  | 'VENCIDO'
  | 'SEM_PROFISSIONAL';
export type SituacaoVersao =
  'EM_ANALISE' | 'APROVADO' | 'RECUSADO' | 'COMPLEMENTAR' | 'SUBSTITUIDO';
export type RegraValidade =
  'VALIDADE_OBRIGATORIA' | 'CERTIDAO_180_DIAS' | 'ATESTADO_30_DIAS' | 'SEM_VALIDADE';
export type Decisao = 'APROVADO' | 'RECUSADO' | 'COMPLEMENTAR';

export interface VersaoAnterior {
  documentoId: string;
  versao: number;
  situacao: SituacaoVersao;
  arquivoNome: string;
  motivo: string | null;
  enviadoEm: string;
  analisadoEm: string | null;
}

export interface ItemDoDossie {
  requisito: string;
  titulo: string;
  descricao: string | null;
  itemEdital: string;
  obrigatorio: boolean;
  regraValidade: RegraValidade;
  enviadoPeloDtp: boolean;
  situacao: SituacaoItem;
  documentoId: string | null;
  versao: number | null;
  arquivoNome: string | null;
  mimeType: string | null;
  tamanhoBytes: number | null;
  emitidoEm: string | null;
  validadeEm: string | null;
  venceEm: string | null;
  enviadoEm: string | null;
  motivo: string | null;
  prazoComplemento: string | null;
  analisadoEm: string | null;
  historico: VersaoAnterior[];
}

export interface FrenteDoDossie {
  frente: Frente;
  situacao: SituacaoFrente;
  itens: ItemDoDossie[];
}

export interface Condutor {
  id: string;
  nome: string;
  cpfMascarado: string;
  dataNascimento: string;
  cnhNumero: string;
  cnhCategoria: 'D' | 'E';
  cnhValidade: string | null;
  crmc: string;
  titular: boolean;
  cadastradoEm: string;
}

export interface Monitor {
  id: string;
  nome: string;
  cpfMascarado: string;
  dataNascimento: string;
  cadastradoEm: string;
}

export interface EventoDaTrilha {
  evento: string;
  origem: 'PRE_CREDENCIADO' | 'DTP' | 'SISTEMA';
  autor: string | null;
  detalhe: Record<string, unknown>;
  criadoEm: string;
}

export interface DossieDetalhe {
  id: string;
  inscricaoId: string;
  submissaoId: string;
  protocolo: string;
  placa: string;
  modalidade: string;
  dreNome: string | null;
  natureza: Natureza;
  statusInscricao: string;
  entidade: { nome: string; documentoMascarado: string };
  situacao: SituacaoDossie;
  aptoEm: string | null;
  versao: number;
  /** Correlação da trinca calculada ao vivo. */
  apto: boolean;
  condutor: Condutor | null;
  monitor: Monitor | null;
  frentes: FrenteDoDossie[];
  resumo: {
    obrigatorios: number;
    aprovados: number;
    emAnalise: number;
    comPendencia: number;
    faltando: number;
  };
  /** Só no DTP. */
  trilha: EventoDaTrilha[] | null;
  /** Só no portal. */
  interesseConfirmado?: boolean;
  podeEnviar?: boolean;
  /** Só no DTP. */
  podeDecidir?: boolean;
}

export interface DossieResumo {
  id: string;
  inscricaoId: string;
  protocolo: string;
  placa: string;
  modalidade: string;
  dreNome: string | null;
  natureza: Natureza;
  entidade: string;
  situacao: SituacaoDossie;
  aptoEm: string | null;
  atualizadoEm: string;
  obrigatorios: number;
  aprovados: number;
  emAnalise: number;
  comPendencia: number;
  temCondutor: boolean;
  temMonitor: boolean;
  /** Só no portal. */
  interesseConfirmado?: boolean;
}

export interface ItemDaFila {
  documentoId: string;
  dossieId: string;
  protocolo: string;
  placa: string;
  entidade: string;
  natureza: Natureza;
  frente: Frente;
  requisito: string;
  titulo: string;
  versao: number;
  arquivoNome: string;
  profissional: string | null;
  /** Documento da ENTIDADE vale para todos os veículos da submissão. */
  veiculosAfetados: number;
  enviadoEm: string;
  diasNaFila: number;
}

export interface Pagina<T> {
  total: number;
  pagina: number;
  tamanho: number;
  itens: T[];
}

export interface FilaDoDtp extends Pagina<ItemDaFila> {
  requisitos: { codigo: string; frente: Frente; titulo: string }[];
}

export interface ProfissionalEntrada {
  nome: string;
  /**
   * Ausente só com `titular: true`: o condutor é o titular da inscrição, e o
   * banco usa o CPF dela — o navegador só conhece o CPF mascarado.
   */
  cpf?: string;
  dataNascimento: string;
  cnhNumero?: string;
  cnhCategoria?: 'D' | 'E';
  cnhValidade?: string;
  crmc?: string;
  titular?: boolean;
}

export interface DocumentoEntrada {
  requisito: string;
  arquivoNome: string;
  conteudoBase64: string;
  emitidoEm?: string;
  validadeEm?: string;
  chaveIdempotencia: string;
}

export interface DecisaoEntrada {
  decisao: Decisao;
  motivo?: string;
  prazoComplemento?: string;
  dossieId?: string;
  chaveIdempotencia: string;
}

// ---------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------

export const ROTULO_FRENTE: Record<Frente, string> = {
  ENTIDADE: 'Empresa ou pessoa',
  VEICULO: 'Veículo',
  CONDUTOR: 'Condutor',
  MONITOR: 'Monitor',
};

export const ROTULO_NATUREZA: Record<Natureza, string> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
  COOPERATIVA: 'Cooperativa',
};

export const ROTULO_SITUACAO_DOSSIE: Record<SituacaoDossie, string> = {
  EM_PREENCHIMENTO: 'Em preenchimento',
  APTO_CONVOCACAO: 'Apto à convocação',
  HABILITADO: 'Habilitado',
  ENCERRADO: 'Encerrado',
};

export const ROTULO_SITUACAO_FRENTE: Record<SituacaoFrente, string> = {
  COMPLETA: 'Completa',
  PENDENTE: 'Pendente',
  SEM_PROFISSIONAL: 'Falta cadastrar',
};

export const ROTULO_SITUACAO_ITEM: Record<SituacaoItem | 'SUBSTITUIDO', string> = {
  PENDENTE: 'Não enviado',
  EM_ANALISE: 'Em análise',
  APROVADO: 'Aprovado',
  RECUSADO: 'Recusado',
  COMPLEMENTAR: 'Complementar',
  VENCIDO: 'Vencido',
  SEM_PROFISSIONAL: 'Cadastre o profissional',
  SUBSTITUIDO: 'Substituído',
};

export const ROTULO_DECISAO: Record<Decisao, string> = {
  APROVADO: 'Aprovar',
  RECUSADO: 'Recusar',
  COMPLEMENTAR: 'Pedir complemento',
};

/** Que datas pedir no envio, pela regra de validade do requisito. */
export const DATAS_DA_REGRA: Record<
  RegraValidade,
  {
    emissao: 'obrigatoria' | 'opcional' | 'nao';
    validade: 'obrigatoria' | 'opcional';
    dica: string;
  }
> = {
  VALIDADE_OBRIGATORIA: {
    emissao: 'opcional',
    validade: 'obrigatoria',
    dica: 'Informe a data de validade que consta no documento.',
  },
  CERTIDAO_180_DIAS: {
    emissao: 'opcional',
    validade: 'opcional',
    dica: 'Informe a validade da certidão. Se ela não tiver, informe a data de emissão: vale por 180 dias.',
  },
  ATESTADO_30_DIAS: {
    emissao: 'obrigatoria',
    validade: 'opcional',
    dica: 'Informe a data de emissão: o atestado precisa ter sido emitido há no máximo 30 dias. Se ele trouxer validade, informe-a também.',
  },
  SEM_VALIDADE: {
    emissao: 'opcional',
    validade: 'opcional',
    dica: 'Se o documento tiver data de validade, informe-a.',
  },
};

/** Tamanho máximo aceito pelo banco (0248): 5 MiB. */
export const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024;
export const TIPOS_ACEITOS = ['application/pdf', 'image/jpeg', 'image/png'] as const;
